import React, { useState, useEffect, useCallback } from 'react';
import {
  getDashboardStats,
  getRevenueReport,
  getRecentAppointments,
  updateAppointmentPaymentStatus,
} from '../../../services/adminService';
import {
  Users, CalendarDays, CreditCard, Stethoscope,
  TrendingUp, TrendingDown, Minus, AlertCircle,
  CheckCircle2, Clock, XCircle, RefreshCw,
} from 'lucide-react';
import { formatDoctorName } from '../../../utils/doctorName';

// ─── helpers ──────────────────────────────────────────────────────────
const MONTHS_ES = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
const fmtCOP = (n) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(n);
const fmtNum = (n) => new Intl.NumberFormat('es-CO').format(n);

const GrowthBadge = ({ pct }) => {
  if (pct === null || pct === undefined) return null;
  const num = parseFloat(pct);
  const pos = num >= 0;
  const Icon = num === 0 ? Minus : pos ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
      num === 0 ? 'bg-gray-100 text-gray-600' : pos ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
    }`}>
      <Icon className="w-3 h-3" />{pos && num !== 0 ? '+' : ''}{num}%
    </span>
  );
};

const StatusBadge = ({ status }) => {
  const map = {
    scheduled:     { label: 'Agendada',   cls: 'bg-blue-100 text-blue-700',    Icon: Clock },
    confirmed:     { label: 'Confirmada', cls: 'bg-indigo-100 text-indigo-700', Icon: CheckCircle2 },
    completed:     { label: 'Completada', cls: 'bg-green-100 text-green-700',   Icon: CheckCircle2 },
    cancelled:     { label: 'Cancelada',  cls: 'bg-red-100 text-red-600',       Icon: XCircle },
    'in-progress': { label: 'En curso',   cls: 'bg-yellow-100 text-yellow-700', Icon: Clock },
  };
  const cfg = map[status] || { label: status, cls: 'bg-gray-100 text-gray-600', Icon: Minus };
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${cfg.cls}`}>
      <cfg.Icon className="w-3 h-3" />{cfg.label}
    </span>
  );
};

const PaymentBadge = ({ status }) => {
  const map = {
    paid:     { label: 'Pagado',  cls: 'bg-green-100 text-green-700',  Icon: CheckCircle2 },
    pending:  { label: 'Pendiente', cls: 'bg-yellow-100 text-yellow-700', Icon: Clock },
    failed:   { label: 'Fallido', cls: 'bg-red-100 text-red-600',      Icon: XCircle },
    refunded: { label: 'Reembolsado', cls: 'bg-gray-100 text-gray-600', Icon: Minus },
  };
  const cfg = map[status] || { label: status || '—', cls: 'bg-gray-100 text-gray-600', Icon: Minus };
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${cfg.cls}`}>
      <cfg.Icon className="w-3 h-3" />{cfg.label}
    </span>
  );
};

const RevenueChart = ({ data }) => {
  if (!data || data.length === 0)
    return <div className="flex items-center justify-center h-40 text-gray-400 text-sm">Sin datos de ingresos aún</div>;

  const W = 600, H = 160, PAD = { top: 10, right: 10, bottom: 32, left: 64 };
  const chartW = W - PAD.left - PAD.right;
  const chartH = H - PAD.top  - PAD.bottom;
  const maxVal = Math.max(...data.map(d => d.totalRevenue), 1);
  const barW   = Math.floor(chartW / data.length * 0.6);
  const gap    = chartW / data.length;
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map(f => ({ value: Math.round(maxVal * f), y: PAD.top + chartH * (1 - f) }));

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" aria-label="Gráfica de ingresos por mes">
      {yTicks.map(t => (
        <g key={t.value}>
          <line x1={PAD.left} x2={PAD.left + chartW} y1={t.y} y2={t.y} stroke="#e5e7eb" strokeWidth="1" />
          <text x={PAD.left - 6} y={t.y + 4} textAnchor="end" fontSize="10" fill="#9ca3af">
            {t.value >= 1_000_000 ? `$${(t.value/1_000_000).toFixed(1)}M` : t.value >= 1_000 ? `$${(t.value/1_000).toFixed(0)}k` : `$${t.value}`}
          </text>
        </g>
      ))}
      {data.map((d, i) => {
        const barH   = Math.max((d.totalRevenue / maxVal) * chartH, 2);
        const bx     = PAD.left + i * gap + (gap - barW) / 2;
        const by     = PAD.top + chartH - barH;
        const isLast = i === data.length - 1;
        return (
          <g key={`${d.year}-${d.month}`}>
            <rect x={bx} y={by} width={barW} height={barH} rx="3" ry="3" fill={isLast ? '#0d9488' : '#99f6e4'} />
            {isLast && (
              <text x={bx + barW / 2} y={by - 4} textAnchor="middle" fontSize="9" fill="#0d9488" fontWeight="600">
                {d.totalRevenue >= 1_000_000 ? `$${(d.totalRevenue/1_000_000).toFixed(1)}M` : `$${(d.totalRevenue/1_000).toFixed(0)}k`}
              </text>
            )}
            <text x={bx + barW / 2} y={PAD.top + chartH + 16} textAnchor="middle" fontSize="10" fill="#6b7280">
              {MONTHS_ES[(d.month - 1) % 12]}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

const KPICard = ({ label, value, sub, growth, icon: Icon, iconBg }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-5 flex flex-col gap-3">
    <div className="flex items-start justify-between">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${iconBg}`}>
        <Icon className="w-5 h-5" />
      </div>
      <GrowthBadge pct={growth} />
    </div>
    <div>
      <p className="text-2xl font-bold text-gray-900 tabular-nums">{value}</p>
      <p className="text-sm font-medium text-gray-700 mt-0.5">{label}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  </div>
);

// ─── Main ─────────────────────────────────────────────────────────────────────────
export default function OverviewSection() {
  const [stats,        setStats]        = useState(null);
  const [revenue,      setRevenue]      = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [refreshing,   setRefreshing]   = useState(false);
  // ID de la cita cuyo pago se esta actualizando (para deshabilitar sus botones).
  const [updatingId,   setUpdatingId]   = useState(null);

  const load = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      // Promise.allSettled: revenue/appointments failures do NOT crash the dashboard.
      // Only getDashboardStats is treated as critical.
      const [sRes, rRes, aRes] = await Promise.allSettled([
        getDashboardStats(),
        getRevenueReport(6),
        getRecentAppointments(8),
      ]);

      if (sRes.status === 'rejected') {
        throw sRes.reason;
      }

      setStats(sRes.value);
      setRevenue(rRes.status === 'fulfilled' ? rRes.value : []);
      setAppointments(aRes.status === 'fulfilled' ? aRes.value : []);
    } catch (e) {
      setError(e?.response?.data?.message || 'Error al cargar el dashboard');
    } finally {
      setLoading(false); setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Marca manualmente el pago de una cita como pagado o fallido y refleja
  // el cambio en la fila correspondiente sin recargar todo el dashboard.
  const handlePaymentStatusChange = async (appointmentId, status) => {
    setUpdatingId(appointmentId);
    try {
      await updateAppointmentPaymentStatus(appointmentId, status);
      setAppointments((prev) => prev.map((appt) => (
        appt._id === appointmentId
          ? { ...appt, payment: { ...appt.payment, status } }
          : appt
      )));
    } catch (e) {
      window.alert(e?.response?.data?.message || 'Error al actualizar el estado de pago');
    } finally {
      setUpdatingId(null);
    }
  };

  if (loading) return (
    <div className="space-y-6 animate-pulse">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => <div key={i} className="bg-gray-100 rounded-xl h-28" />)}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-gray-100 rounded-xl h-56" />
        <div className="bg-gray-100 rounded-xl h-56" />
      </div>
    </div>
  );

  if (error) return (
    <div className="flex flex-col items-center justify-center h-64 gap-4 text-red-500">
      <AlertCircle className="w-10 h-10" />
      <p className="text-sm">{error}</p>
      <button onClick={() => load()} className="text-sm text-teal-600 hover:underline">Reintentar</button>
    </div>
  );

  const kpis = [
    { label: 'Usuarios totales',   value: fmtNum(stats.users.total),             sub: `${fmtNum(stats.users.doctors)} médicos · ${fmtNum(stats.users.patients)} pacientes`, growth: null,                       icon: Users,       iconBg: 'bg-blue-50 text-blue-600' },
    { label: 'Citas este mes',     value: fmtNum(stats.appointments.thisMonth),  sub: `${fmtNum(stats.appointments.today)} hoy`,                                           growth: stats.appointments.growthPct, icon: CalendarDays, iconBg: 'bg-purple-50 text-purple-600' },
    { label: 'Ingresos del mes',   value: fmtCOP(stats.revenue.thisMonth),       sub: `Total acumulado: ${fmtCOP(stats.revenue.total)}`,                                   growth: stats.revenue.growthPct,      icon: CreditCard,   iconBg: 'bg-green-50 text-green-600' },
    { label: 'Médicos pendientes', value: fmtNum(stats.users.pendingDoctors),    sub: `${fmtNum(stats.users.verifiedDoctors)} verificados`,                               growth: null,                         icon: Stethoscope,  iconBg: stats.users.pendingDoctors > 0 ? 'bg-yellow-50 text-yellow-600' : 'bg-teal-50 text-teal-600' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Resumen General</h2>
          <p className="text-sm text-gray-500">Datos en tiempo real desde la base de datos</p>
        </div>
        <button onClick={() => load(true)} disabled={refreshing}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-teal-600 border border-gray-200 rounded-lg px-3 py-1.5 hover:border-teal-300 transition-colors">
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />Actualizar
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map(k => <KPICard key={k.label} {...k} />)}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-800">Ingresos por mes</h3>
              <p className="text-xs text-gray-400">6 últimos meses · COP</p>
            </div>
            {revenue.length > 0 && (
              <span className="text-xs font-medium text-teal-700 bg-teal-50 px-2 py-0.5 rounded-full">
                {fmtCOP(revenue[revenue.length - 1]?.totalRevenue || 0)} este mes
              </span>
            )}
          </div>
          <RevenueChart data={revenue} />
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-800 mb-4">Estado de citas</h3>
          <div className="space-y-3">
            {[
              { label: 'Completadas', value: stats.appointments.completed, color: 'bg-green-500' },
              { label: 'Activas',     value: stats.appointments.active,    color: 'bg-blue-500'  },
              { label: 'Canceladas',  value: stats.appointments.cancelled, color: 'bg-red-400'   },
            ].map(({ label, value, color }) => {
              const pct = stats.appointments.total > 0 ? Math.round((value / stats.appointments.total) * 100) : 0;
              return (
                <div key={label}>
                  <div className="flex justify-between text-xs text-gray-600 mb-1">
                    <span>{label}</span>
                    <span className="font-medium tabular-nums">{fmtNum(value)} ({pct}%)</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-1.5">
                    <div className={`${color} h-1.5 rounded-full transition-all duration-500`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
            <div className="pt-2 border-t border-gray-100">
              <p className="text-xs text-gray-400">Total: {fmtNum(stats.appointments.total)} citas</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-800">Citas recientes</h3>
          <span className="text-xs text-gray-400">últimas {appointments.length}</span>
        </div>
        {appointments.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-400">
            <CalendarDays className="w-8 h-8 mb-2 text-gray-200" />
            <p className="text-sm">No hay citas registradas aún</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>{['Paciente','Médico','Fecha','Estado','Pago',''].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">{h}</th>
                ))}</tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {appointments.map(appt => {
                  const patientName = appt.patient?.user?.profile ? `${appt.patient.user.profile.firstName} ${appt.patient.user.profile.lastName}` : appt.patient?.user?.email || 'N/A';
                  const doctorName  = formatDoctorName(appt.doctor, appt.doctor?.user?.email || 'N/A');
                  const dateStr     = appt.scheduledDate ? new Date(appt.scheduledDate).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
                  const paymentStatus = appt.payment?.status;
                  const canEditPayment = paymentStatus === 'pending' || paymentStatus === 'failed';
                  const isRowUpdating  = updatingId === appt._id;
                  return (
                    <tr key={appt._id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-800 font-medium">{patientName}</td>
                      <td className="px-4 py-3 text-gray-600">{doctorName}</td>
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{dateStr}</td>
                      <td className="px-4 py-3"><StatusBadge status={appt.status} /></td>
                      <td className="px-4 py-3"><PaymentBadge status={paymentStatus} /></td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {canEditPayment && (
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              disabled={isRowUpdating}
                              onClick={() => handlePaymentStatusChange(appt._id, 'paid')}
                              className="text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 disabled:opacity-50 disabled:cursor-not-allowed rounded-md px-2 py-1 transition-colors"
                              title="Marcar pago como pagado manualmente"
                            >
                              Marcar pagado
                            </button>
                            <button
                              type="button"
                              disabled={isRowUpdating}
                              onClick={() => handlePaymentStatusChange(appt._id, 'failed')}
                              className="text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed rounded-md px-2 py-1 transition-colors"
                              title="Marcar pago como fallido manualmente"
                            >
                              Marcar fallido
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
